import { useEffect, useRef } from "react";
import { PhoneOff, Mic, MicOff, Video, VideoOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useProfile } from "@/hooks/useFriends";
import { mediaUrl } from "@/lib/api";
import { useGroupCall, type GroupCallParticipant } from "@/hooks/useGroupCall";
import { cn } from "@/lib/utils";

function ParticipantTile({ participant, withVideo }: { participant: GroupCallParticipant; withVideo: boolean }) {
  const { data: profile } = useProfile(participant.userId);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = participant.stream;
  }, [participant.stream]);

  const showVideo = withVideo && participant.stream && participant.stream.getVideoTracks().length > 0;

  return (
    <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-secondary">
      {showVideo ? (
        <video ref={videoRef} autoPlay playsInline className="h-full w-full object-cover" />
      ) : (
        <div className="flex flex-col items-center gap-2 text-white">
          <Avatar className="h-16 w-16">
            <AvatarImage src={mediaUrl(profile?.avatar_url)} />
            <AvatarFallback className="text-xl">{profile?.display_name?.[0] ?? "?"}</AvatarFallback>
          </Avatar>
          {withVideo && <video ref={videoRef} autoPlay playsInline className="hidden" />}
        </div>
      )}
      <span className="absolute bottom-1 left-1.5 rounded bg-black/50 px-1.5 py-0.5 text-xs text-white">
        {profile?.display_name ?? (participant.stream ? "…" : "Connecting…")}
      </span>
    </div>
  );
}

/**
 * Global group-call overlay, mounted once in AppLayout alongside the 1:1
 * CallOverlay - deliberately separate files/components, not merged, since
 * these are two independent systems. Renders nothing while status is "idle".
 */
export default function GroupCallOverlay() {
  const { status, withVideo, localStream, participants, isMuted, isCameraOff, leaveGroupCall, toggleMute, toggleCamera } = useGroupCall();

  const localVideoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (localVideoRef.current) localVideoRef.current.srcObject = localStream;
  }, [localStream]);

  if (status === "idle") return null;

  const gridCols = participants.length <= 1 ? "grid-cols-1" : participants.length <= 4 ? "grid-cols-2" : "grid-cols-3";

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      {status === "connecting" && participants.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-white">
          <div className="text-lg font-semibold">Joining call…</div>
        </div>
      ) : (
        <div className={cn("grid flex-1 gap-1 overflow-y-auto p-1", gridCols)}>
          {participants.map((p) => (
            <ParticipantTile key={p.userId} participant={p} withVideo={withVideo} />
          ))}
        </div>
      )}

      {withVideo && (
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className="absolute right-4 top-4 h-32 w-24 rounded-lg border border-white/20 object-cover shadow-lg"
        />
      )}

      <div className="flex items-center justify-center gap-6 bg-black/40 py-6">
        <div className="flex flex-col items-center gap-2">
          <Button size="icon" variant="secondary" className="h-12 w-12 rounded-full" onClick={toggleMute}>
            {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </Button>
          <span className="text-xs text-white/70">{isMuted ? "Unmute" : "Mute"}</span>
        </div>
        {withVideo && (
          <div className="flex flex-col items-center gap-2">
            <Button size="icon" variant="secondary" className="h-12 w-12 rounded-full" onClick={toggleCamera}>
              {isCameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
            </Button>
            <span className="text-xs text-white/70">{isCameraOff ? "Start video" : "Stop video"}</span>
          </div>
        )}
        <div className="flex flex-col items-center gap-2">
          <Button size="icon" variant="destructive" className="h-12 w-12 rounded-full" onClick={leaveGroupCall}>
            <PhoneOff className="h-5 w-5" />
          </Button>
          <span className="text-xs text-white/70">Leave</span>
        </div>
      </div>
    </div>
  );
}
